import React, { useState, useContext, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import { EnvelopeFill } from 'react-bootstrap-icons';
import TermsConditionsModal from '../about/TermsConditionsModal';
import PrivacyPolicyModal from '../about/PrivacyPolicyModal';
import { AppContext } from '../window';
import { AuthorizedUser } from '../models/User';
import Loader from '../libs/Loader';
import { NotificationContext } from '../context/NotificationContext';
import RecaptchaV2, { RecaptchaV2Handle } from '../libs/RecaptchaV2';
import { logDataLayerEvent } from '../utils/tagManager';
import { useTranslation, Trans } from '../i18n';
import { useTracking } from '../hooks/useTracking';
import { setPendingTempPassword } from './PendingPasswordPrompt';
import { markDisclaimerAccepted } from '../hooks/useDisclaimer';
import { updateCsrfMetaToken } from '../api/fetch';
import { defaultBirthDateParts } from '../components/BirthDatePicker';
import { suggestEmail } from '../utils/emailTypo';
import LoginModal from '../user/LoginModal';
import RequestResetModal from '../user/RequestResetModal';

interface EmailFirstSignupProps {
    onSignupSuccess?: () => void;
    submitButtonText?: string;
    initialEmail?: string;
}

const TEMP_PASSWORD_LENGTH = 16;
const PASSWORD_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const MAX_NICKNAME_RETRIES = 3;
const NICKNAME_MAX = 24;
const NICKNAME_MIN = 4;

const generateTempPassword = (): string => {
    const arr = new Uint32Array(TEMP_PASSWORD_LENGTH);
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(arr);
    } else {
        for (let i = 0; i < TEMP_PASSWORD_LENGTH; i++) {
            arr[i] = Math.floor(Math.random() * 0xffffffff);
        }
    }
    let out = '';
    for (let i = 0; i < TEMP_PASSWORD_LENGTH; i++) {
        out += PASSWORD_CHARS.charAt(arr[i]! % PASSWORD_CHARS.length);
    }
    return out;
};

const randomDigits = (count: number): string => {
    let out = '';
    for (let i = 0; i < count; i++) {
        out += Math.floor(Math.random() * 10).toString();
    }
    return out;
};

export const nicknameFromEmail = (email: string): string => {
    const local = (email.split('@')[0] || '').replace(/[^A-Za-z0-9]/g, '');
    let nick = local.slice(0, NICKNAME_MAX);
    if (nick.length < NICKNAME_MIN) {
        nick = (nick + randomDigits(NICKNAME_MIN - nick.length)).slice(0, NICKNAME_MAX);
    }
    return nick;
};

const appendSuffix = (nickname: string, suffix: string): string => {
    const base = nickname.slice(0, Math.max(1, NICKNAME_MAX - suffix.length));
    return (base + suffix).slice(0, NICKNAME_MAX);
};

/**
 * Whether the signup failed on the generated nickname alone, so a retry with
 * another one can succeed. The server lists every error by form and attribute
 * (`errors.User.nickname`); an older response only has the first message.
 */
export const isOnlyNicknameError = (responseData: any): boolean => {
    const errors = responseData?.errors;
    if (errors && typeof errors === 'object') {
        const forms = Object.keys(errors);
        return forms.length === 1 && forms[0] === 'User'
            && Object.keys(errors.User ?? {}).length === 1 && Boolean(errors.User.nickname);
    }
    const lastError = responseData?.error;
    return typeof lastError === 'string' && /nick|name|gebruikersnaam|benutzer/i.test(lastError);
};

const EmailFirstSignup: React.FC<EmailFirstSignupProps> = ({ onSignupSuccess, submitButtonText, initialEmail }) => {
    const { t } = useTranslation();
    const track = useTracking();
    const appContext = useContext(AppContext);
    const { addNotification } = useContext(NotificationContext);
    const recaptchaRef = useRef<RecaptchaV2Handle>(null);
    const hasInteracted = useRef(false);

    const [email, setEmail] = useState(initialEmail ?? '');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // A likely correction of the address ("gmial.com" -> "gmail.com"), from
    // the typo check on blur or the server's address check; never blocks
    const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
    // The address already has an account: offer log in and forgot password
    const [alreadyRegistered, setAlreadyRegistered] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);

    // Hold the temp password across the recaptcha round-trip
    const tempPasswordRef = useRef<string>('');
    const recaptchaTokenRef = useRef<string | null>(null);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasInteracted.current) {
            hasInteracted.current = true;
            track('signup_form_interact');
        }
        setEmail(e.target.value);
        setAlreadyRegistered(false);
        if (emailSuggestion && !suggestEmail(e.target.value)) {
            setEmailSuggestion(null);
        }
    };

    const handleEmailBlur = () => {
        setEmailSuggestion(suggestEmail(email));
    };

    const applyEmailSuggestion = (e: React.MouseEvent) => {
        e.preventDefault();
        if (emailSuggestion) {
            setEmail(emailSuggestion);
            setEmailSuggestion(null);
        }
    };

    const submitSignup = async (token: string | null) => {
        setIsSubmitting(true);
        const birth = defaultBirthDateParts();
        const tempPassword = tempPasswordRef.current || generateTempPassword();
        tempPasswordRef.current = tempPassword;

        let nickname = nicknameFromEmail(email);
        let lastError: string | null = null;

        for (let attempt = 0; attempt < MAX_NICKNAME_RETRIES; attempt++) {
            const submitData = new URLSearchParams();
            submitData.append('UserCredentials[email]', email);
            submitData.append('UserCredentials[password]', tempPassword);
            submitData.append('User[nickname]', nickname);
            submitData.append('User[day]', birth.day);
            submitData.append('User[month]', birth.month);
            submitData.append('User[year]', birth.year);
            submitData.append('User[gender]', 'male');
            submitData.append('User[tcAgree]', '1');
            if (token) {
                submitData.append('g-recaptcha-response', token);
            }

            let responseData: any;
            try {
                const response = await fetch('/user/signup/do?json=1&initialPasswordPending=1', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: submitData.toString(),
                });
                responseData = await response.json();
            } catch (err) {
                console.error('Signup error:', err);
                lastError = t('auth.error_occurred');
                break;
            }

            if (responseData?.success) {
                updateCsrfMetaToken(responseData.csrfToken);
                const responseUser = responseData.user as AuthorizedUser | undefined;
                addNotification({
                    level: 'success',
                    message: t('auth.signup_success'),
                });
                logDataLayerEvent('signup', {}, responseUser ?? appContext.user);
                // The signup form's tos_agree checkbox already captured the
                // 18+/cookies/T&C consent that the DisclaimerModal asks for,
                // so mark it accepted to prevent the modal popping after
                // AuthRedirect lands the user on /search (and on later visits
                // from the same browser).
                markDisclaimerAccepted();
                // Stash the random password so PendingPasswordPrompt can show
                // the SetPasswordModal *after* the user has been redirected to
                // the logged-in experience (via AuthRedirect on setUser).
                setPendingTempPassword(tempPassword);
                if (responseUser) {
                    appContext.setUser(responseUser);
                }
                setIsSubmitting(false);
                if (onSignupSuccess) {
                    onSignupSuccess();
                }
                return;
            }

            lastError = responseData?.error || t('auth.registration_failed');
            setAlreadyRegistered(responseData?.errorCode === 'email_already_registered');
            if (typeof responseData?.emailSuggestion === 'string' && responseData.emailSuggestion !== email) {
                setEmailSuggestion(responseData.emailSuggestion);
            }
            const isNicknameConflict = isOnlyNicknameError(responseData);
            if (!isNicknameConflict) {
                break;
            }
            // Retry with a digit suffix appended to the nickname
            nickname = appendSuffix(nicknameFromEmail(email), randomDigits(4));
        }

        setErrorMessage(lastError || t('auth.registration_failed'));
        setIsSubmitting(false);
        if (recaptchaRef.current?.reset) {
            recaptchaRef.current.reset();
        }
        recaptchaTokenRef.current = null;
    };

    const handleRecaptchaVerify = (token: string) => {
        recaptchaTokenRef.current = token;
        if (token) {
            submitSignup(token);
        }
    };

    const handleRecaptchaExpire = () => {
        recaptchaTokenRef.current = null;
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);

        if (!email || !email.includes('@')) {
            setErrorMessage(t('auth.validation_valid_email'));
            return;
        }
        if (!agreeToTerms) {
            setErrorMessage(t('auth.validation_agree_terms'));
            return;
        }

        track('signup_form_submit');

        // Generate the temp password before recaptcha so we can keep it through round-trip
        tempPasswordRef.current = generateTempPassword();

        if (appContext.recaptchaSiteKey && !recaptchaTokenRef.current) {
            if (recaptchaRef.current?.execute) {
                recaptchaRef.current.execute();
                return;
            }
        }

        submitSignup(recaptchaTokenRef.current);
    };

    return (
        <div className="email-first-signup">
            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            {alreadyRegistered && !isSubmitting && (
                <div className="d-flex gap-2 mb-3" data-testid="already-registered">
                    <Button variant="primary" onClick={() => setShowLogin(true)}>{t('auth.login')}</Button>
                    <Button variant="outline-secondary" onClick={() => setShowReset(true)}>{t('auth.forgot_password')}</Button>
                </div>
            )}
            {isSubmitting && <Loader />}

            <Form onSubmit={handleSubmit} className={isSubmitting ? 'd-none' : ''}>
                <InputGroup className="mb-3">
                    <InputGroup.Text>
                        <EnvelopeFill />
                    </InputGroup.Text>
                    <Form.Floating>
                        <Form.Control
                            type="email"
                            name="email"
                            placeholder={t('landing_d.email_placeholder')}
                            value={email}
                            onChange={handleEmailChange}
                            onBlur={handleEmailBlur}
                            required
                            autoFocus
                        />
                        <Form.Label>{t('landing_d.email_placeholder')}</Form.Label>
                    </Form.Floating>
                </InputGroup>
                {emailSuggestion && (
                    <div className="form-text email-suggestion mt-n2 mb-3" data-testid="email-suggestion">
                        <Trans
                            i18nKey="auth.email_did_you_mean"
                            values={{ suggestion: emailSuggestion }}
                            components={{
                                fix: <a href="#" className="email-suggestion-apply" onClick={applyEmailSuggestion} />,
                            }}
                        />
                    </div>
                )}

                <Form.Check
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    label={
                        <span style={{ fontSize: '0.875rem' }}>
                            <Trans
                                i18nKey="auth.tos_agree"
                                components={{
                                    termsLink: (
                                        <a
                                            href="#"
                                            style={{ fontSize: '0.875rem' }}
                                            onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                                        />
                                    ),
                                    privacyLink: (
                                        <a
                                            href="#"
                                            style={{ fontSize: '0.875rem' }}
                                            onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
                                        />
                                    ),
                                }}
                            />
                        </span>
                    }
                    className="mb-3 form-check-lg"
                    required
                />

                {appContext.recaptchaSiteKey && (
                    <RecaptchaV2
                        ref={recaptchaRef}
                        siteKey={appContext.recaptchaSiteKey}
                        onVerify={handleRecaptchaVerify}
                        onExpire={handleRecaptchaExpire}
                        invisible={true}
                    />
                )}

                <Button
                    variant="success"
                    type="submit"
                    className="w-100"
                    disabled={isSubmitting}
                    size="lg"
                >
                    {isSubmitting ? t('auth.creating_account') : (submitButtonText || t('landing_d.submit'))}
                </Button>
            </Form>

            <LoginModal
                show={showLogin}
                onHide={() => setShowLogin(false)}
                initialEmail={email}
                onShowPasswordReset={() => { setShowLogin(false); setShowReset(true); }}
            />
            <RequestResetModal
                show={showReset}
                onHide={() => setShowReset(false)}
                initialEmail={email}
                onShowLogin={() => { setShowReset(false); setShowLogin(true); }}
            />
            <TermsConditionsModal show={showTerms} onHide={() => setShowTerms(false)} />
            <PrivacyPolicyModal show={showPrivacy} onHide={() => setShowPrivacy(false)} />
        </div>
    );
};

export default EmailFirstSignup;
